import localforage from "localforage";

// Archivio offline, separato per tipo. Ogni tipo ha due chiavi in localforage (IndexedDB):
//  - archivio_<tipo>: array dei record (stessa forma restituita da /api/archivio)
//  - archivio_<tipo>_meta: { schemaVersion, owner, since, lastFull, lastSync, partial, beforeId }
// I record restano anche in una cache in memoria, così lista e dettaglio non rileggono IndexedDB.

export const TIPI = ["ordini", "offerte"];
export const SCHEMA_VERSION = 1;

const dataKey = tipo => "archivio_" + tipo;
const metaKey = tipo => "archivio_" + tipo + "_meta";

const cache = {};

export const tipoFromProposta = proposta => (proposta == 1 ? "ordini" : "offerte");

export const labelTipo = tipo => (tipo === "ordini" ? "Ordini" : "Offerte");

export const nowTs = () => Math.floor(Date.now() / 1000);

// Identifica l'utente proprietario della cache senza salvarne il token
export const currentOwner = () => {
  try {
    const token = JSON.parse(localStorage.getItem("authUser")).api_token || "";
    let hash = 5381;
    for (let i = 0; i < token.length; i++) {
      hash = ((hash << 5) + hash + token.charCodeAt(i)) | 0;
    }
    return String(hash);
  } catch (e) {
    return "";
  }
};

export async function loadArchivio(tipo) {
  if (cache[tipo]) return cache[tipo];
  const records = await localforage.getItem(dataKey(tipo));
  if (records) cache[tipo] = records;
  return records;
}

export const getCachedArchivio = tipo => cache[tipo] || null;

// Aggiorna solo la cache in memoria (download in corso, non ancora salvato su IndexedDB)
export const setCachedArchivio = (tipo, records) => {
  if (records) cache[tipo] = records;
  else delete cache[tipo];
};

export async function loadMeta(tipo) {
  return (await localforage.getItem(metaKey(tipo))) || {};
}

export async function saveMeta(tipo, meta) {
  await localforage.setItem(metaKey(tipo), meta);
}

export async function saveArchivio(tipo, records, meta) {
  cache[tipo] = records;
  await localforage.setItem(dataKey(tipo), records);
  if (meta) await saveMeta(tipo, meta);
}

// Applica un delta: i record cambiati sostituiscono quelli con lo stesso id, gli eliminati vengono rimossi
export function mergeDelta(records, changed, deletedIds) {
  const byId = new Map();
  (records || []).forEach(r => byId.set(r.id, r));
  (deletedIds || []).forEach(id => byId.delete(id));
  (changed || []).forEach(r => byId.set(r.id, r));
  return Array.from(byId.values());
}

export const getCachedRecord = (tipo, id) => {
  const records = cache[tipo];
  return records ? records.find(r => r.id == id) || null : null;
};

export async function getRecordById(tipo, id) {
  const records = await loadArchivio(tipo);
  return records ? records.find(r => r.id == id) || null : null;
}

// Modifica locale (es. tag salvati o record eliminato) in attesa del prossimo delta
export async function updateRecord(tipo, id, patch) {
  const records = await loadArchivio(tipo);
  if (!records) return;
  await saveArchivio(tipo, records.map(r => (r.id == id ? { ...r, ...patch } : r)));
}

// Come DELETE /api/order con "code": elimina il codice e le sue revisioni "code.N"
export async function removeRecordsByCode(tipo, code) {
  const records = await loadArchivio(tipo);
  if (!records) return;
  await saveArchivio(tipo, records.filter(r => r.code !== code && !(r.code && r.code.indexOf(code + ".") === 0)));
}

export async function removeRecordById(tipo, id) {
  const records = await loadArchivio(tipo);
  if (!records) return;
  await saveArchivio(tipo, records.filter(r => r.id != id));
}

export async function clearArchivio() {
  for (const tipo of TIPI) {
    delete cache[tipo];
    await localforage.removeItem(dataKey(tipo));
    await localforage.removeItem(metaKey(tipo));
  }
}

// Rimuove l'archivio unico delle versioni precedenti
export async function clearLegacyArchivio() {
  localStorage.removeItem("archivioTS");
  localStorage.removeItem("firstArchivioTS");
  localStorage.removeItem("archivePage");
  localStorage.removeItem("ht-dettaglio-archivio");
  await localforage.removeItem("archivio");
}
